import { Response } from 'application/Shared/Helpers';
import { debug as logger } from 'application/Shared/Utils';
import { FetchRecipeParams, Recipe } from 'application/Shared/types/recipe';
import axios from 'axios';
import { useEffect, useState } from 'react';

interface RecipeResponseData {
    data: Recipe[];
    message: string;
    serverResponse: number;
}

/**
 * Custom hook for getting the details of a customer recipe.
 *
 * @param {FetchRecipeParams} params - The parameters for fetching the recipes
 * @param {string} params.platformTenantId - The ID of the platform tenant
 * @param {RECIPE_ACTIONS} params.action - The action to run
 * @param {string} params.applicationId - The ID of the application
 *
 * @returns An object containing the recipe details and loading status
 *
 * @example
 * const { data, error, loading } = useFetchRecipes({
 *    platformTenantId: '1234',
 *    action: 'offboard',
 *    applicationId: '5678'
 * });
 */
export function useFetchRecipes(params: FetchRecipeParams) {
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<Recipe[]>([]);

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                // Check for required parameters, and throw if they are missing.
                if (!params.platformTenantId) {
                    throw new Error('Missing required parameters');
                }

                const response = await axios.get<RecipeResponseData>(
                    '/recipe',
                    {
                        cancelToken: source.token,
                        params
                    }
                );
                if (!Response.hasData(response)) {
                    logger.w('No recipes found.', response);
                } else {
                    const data = Response.getData(response);

                    setData(data);
                }
            } catch (error) {
                logger.error('Error fetching recipes.', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            source.cancel();
        };
    }, [params]);

    return { loading, recipes: data };
}
